import React, { useState } from 'react';
import {
  Rocket, MessageSquare,
  ClipboardList, GitBranch, Sliders, Code, Presentation,
  ArrowRight, Zap, Play, AlertCircle, Lightbulb, Info,
  BarChart3, FileText, Users, Mail, Search, Shield
} from 'lucide-react';
import CountUp from './reactbits/CountUp';
import SpotlightCard from './reactbits/SpotlightCard';
import FadeIn from './reactbits/FadeIn';
import RotatingText from './reactbits/RotatingText';
import DecryptedText from './reactbits/DecryptedText';
import StarBorder from './reactbits/StarBorder';
import TrueFocus from './reactbits/TrueFocus';
import Stepper, { Step } from './reactbits/Stepper';
import FloatingLines from './reactbits/FloatingLines';
import Marquee from './reactbits/Marquee';

// ── Section content ──────────────────────────────────────────────────

const STEPS = [
  {
    icon: ClipboardList,
    title: 'Describe Your Agent',
    askQuestion: 'How does the questionnaire work? What kind of details should I provide?',
    summary: 'Tell Byte what you want to build in plain English. An adaptive questionnaire captures requirements, scores them for clarity, and asks follow-ups until every detail is nailed down.',
    content: [
      { type: 'heading', text: 'Starting a conversation' },
      { type: 'paragraph', text: 'Type a natural-language message in the chat — something like "Build an email triage agent that categorizes emails by urgency and drafts responses." Byte analyzes your message for build intent. If the intent is clear, a tailored questionnaire is generated immediately. If the message is ambiguous or conversational, Byte responds naturally and offers a "Build Agent?" button you can click when you\'re ready to proceed.' },
      { type: 'paragraph', text: 'The chat panel is flexible — dock it to either side of the screen for a split view, float it as a draggable overlay, or minimize it to a compact bottom bar. Double-click the header to toggle between docked and floating. When floating, drag it near the left or right edge and it snaps into a docked position automatically. All modes keep full conversation history.' },

      { type: 'heading', text: 'The adaptive questionnaire' },
      { type: 'paragraph', text: 'Once build intent is detected, Byte generates a structured questionnaire with multiple field types, each tailored to the kind of information needed:' },
      { type: 'list', items: [
        'Text fields — for descriptions, names, and open-ended details. Each comes pre-filled with a realistic example value.',
        'Dropdowns — for choosing from predefined options (e.g., output format, integration type).',
        'Radio buttons — for mutually exclusive choices (e.g., "Real-time or batch processing?").',
        'Checkboxes — for selecting multiple options (e.g., which channels to support).',
        'Copy-to-fill buttons — click the example value badge next to any field to paste it instantly.',
      ]},
      { type: 'paragraph', text: 'Every field includes a label, a helper description, and a pre-filled example. You can accept the examples as-is for a quick start, or customize each answer. The questionnaire adapts — different inputs produce different follow-up questions.' },

      { type: 'heading', text: 'Clarity scoring and follow-ups' },
      { type: 'paragraph', text: 'After you submit, Byte runs a clarity analysis on your answers and returns a score from 0 to 100%. The score is displayed as a progress ring next to the questionnaire. If the score falls below 75%, a "More Details Needed" section appears listing exactly which answers need refinement — for example, "What diagnostic questions should the AI ask?" or "What format should the output report use?"' },
      { type: 'paragraph', text: 'Each unclear item includes a text area where you can provide the missing detail. Once you submit the refinements, Byte re-scores. This loop continues until clarity passes the threshold, at which point Byte auto-advances to the next step. You can always go back, edit previous answers, and regenerate the questionnaire from scratch.' },

      { type: 'heading', text: 'Auto-pilot mode' },
      { type: 'paragraph', text: 'Toggle "Let Byte handle it" to activate auto-pilot. In this mode, Byte runs the entire pipeline hands-free — from questionnaire submission through flow decomposition, schema generation, variable mapping, prompt generation, and execution — with animated 3-2-1 countdown transitions between each step. You can pause at any time, take over manually, or resume auto-pilot from wherever you stopped.' },

      { type: 'callout', variant: 'tip', text: 'Be specific in your initial description. "Analyze support emails, categorize by urgency (high/medium/low), and draft a response" works much better than "process emails." The more detail you give upfront, the fewer follow-up rounds Byte will need.' },
    ],
  },
  {
    icon: GitBranch,
    title: 'Review the Plan',
    askQuestion: 'What is the workflow view? How do I read and edit the flow diagram?',
    summary: 'Byte decomposes your requirements into a visual node-based workflow. Three architecture options are generated — pick the best fit, then optionally edit any node with AI-validated changes.',
    content: [
      { type: 'heading', text: 'Three architecture options' },
      { type: 'paragraph', text: 'After requirements are finalized, Byte generates three distinct workflow architectures. Each is a complete pipeline of connected nodes, but optimized for different priorities:' },
      { type: 'list', items: [
        'Recommended — a balanced architecture with the right number of nodes for most use cases. This is pre-selected by default.',
        'Simple — fewer nodes, faster execution, lower cost. Best when you need a quick MVP or the task is straightforward.',
        'Thorough — additional validation and enrichment layers. Choose this when accuracy is critical or the domain is complex.',
      ]},
      { type: 'paragraph', text: 'Switch between architectures using the tabbed selector at the top. Each tab shows the flow label, step count, and a brief description. The flow diagram updates instantly when you switch. A colored tick icon indicates the currently selected architecture.' },

      { type: 'heading', text: 'Reading the flow diagram' },
      { type: 'paragraph', text: 'The visual pipeline displays nodes as connected cards with color-coded types:' },
      { type: 'list', items: [
        'Cyan (Inbound) — receives input data into the workflow. Always the first node.',
        'Blue (LLM) — AI processing nodes that transform, analyze, or generate content.',
        'Red (Integration) — connects to external APIs or services (e.g., email, Slack, databases).',
        'Green (Outbound) — delivers the final output. Always the last node.',
      ]},
      { type: 'paragraph', text: 'Click any node in the diagram to open its detail panel. The panel contains four tabs you can switch between:' },
      { type: 'list', items: [
        'Node Details — display name, business function description, and estimated processing cost.',
        'Data — data-flow indicators showing what comes into the node and what goes out.',
        'Prompts — a preview of the AI instructions that will be generated for this node.',
        'Input/Output — schema hints for the expected input and output fields.',
      ]},
      { type: 'paragraph', text: 'Use the "Node X of Y" indicator to navigate between nodes without closing the detail panel. The panel header also displays cost and time estimates for the overall workflow.' },

      { type: 'heading', text: 'Editing nodes before finalizing' },
      { type: 'paragraph', text: 'Click "Edit Flow" to enter edit mode. The diagram becomes interactive — you can modify any node in the pipeline:' },
      { type: 'list', items: [
        'Rename a node — change its display name to something more meaningful.',
        'Change its description — update what the node does.',
        'Add a node — insert a new processing step between existing nodes (up to 5 nodes max).',
        'Remove a node — delete unnecessary steps from the pipeline.',
      ]},
      { type: 'paragraph', text: 'Every change you make is validated by AI. Click the "Validate" button on any edited node — Byte checks whether the change is coherent with the rest of the workflow. If something doesn\'t fit (e.g., a node description conflicts with its neighbors), Byte suggests an alternative you can apply with one click. When you edit a node, its neighbors are automatically re-validated to ensure the whole pipeline stays consistent.' },

      { type: 'heading', text: 'Finalizing the plan' },
      { type: 'paragraph', text: 'Once you\'re satisfied with the architecture, click "Finalize & Generate Schemas." This locks in your selected flow and edited nodes, then triggers schema generation — creating typed input/output field definitions for every node. The status changes to indicate the agent is being created, and you\'re automatically advanced to the data mapping step.' },

      { type: 'callout', variant: 'tip', text: 'The Recommended plan works for most use cases. Only switch to Thorough when you need extra validation layers (e.g., compliance checks, multi-stage review). You can always come back and edit — Byte re-validates every change automatically.' },
    ],
  },
  {
    icon: Sliders,
    title: 'Wire Your Data',
    askQuestion: 'What is data mapping? How does data flow between nodes?',
    summary: 'Byte generates typed schemas for every node and automatically maps how data flows between them. Review the Variable Dictionary and test data to verify everything is wired correctly.',
    content: [
      { type: 'heading', text: 'Schema generation' },
      { type: 'paragraph', text: 'After the plan is finalized, Byte generates typed input and output schemas for every node in the pipeline. Each schema contains 3–5 fields with meaningful camelCase names that reflect their purpose — for example, an email triage node might have input fields like rawEmailText and senderAddress, and output fields like urgencyLevel, departmentCategory, and draftResponse.' },
      { type: 'paragraph', text: 'Each field has a type drawn from six supported types:' },
      { type: 'list', items: [
        'string — text values (names, descriptions, content)',
        'int — whole numbers (counts, IDs, scores)',
        'float — decimal numbers (percentages, ratings, prices)',
        'boolean — true/false flags',
        'array — lists of values',
        'datetime — timestamps and dates',
      ]},
      { type: 'paragraph', text: 'Field names are unique across the entire workflow — no two nodes share a field name. This prevents ambiguity when data flows between nodes downstream.' },

      { type: 'heading', text: 'Variable mapping' },
      { type: 'paragraph', text: 'Once schemas are generated, Byte maps how data flows from node to node. For each downstream node\'s input field, it identifies which upstream node\'s output field provides the data. The result is a fully-wired pipeline where data passes automatically from one step to the next without manual configuration.' },
      { type: 'paragraph', text: 'Integration nodes have special handling. Their integrationOutput fields (data received from an external API) are available during processing but are not listed in nodeInput — they arrive at runtime, not from upstream nodes.' },

      { type: 'heading', text: 'Variable Dictionary' },
      { type: 'paragraph', text: 'The Variable Dictionary displays every variable in the pipeline in a two-column grid. Each entry shows:' },
      { type: 'list', items: [
        'Variable name — displayed in monospace with syntax highlighting.',
        'Type badge — color-coded label (string, int, float, boolean, array, datetime).',
        'Source — where the data originates: "Agent Input" for the first node, or "Node N" for downstream data.',
        'Example value — a realistic sample showing what the field looks like in practice. Long values are truncated with a hover tooltip to see the full content.',
      ]},
      { type: 'paragraph', text: 'The dictionary initially shows the first six variables. Click "Show All" to expand the full list. This gives you a single-page overview of all data flowing through the agent without having to click through each node individually.' },

      { type: 'heading', text: 'Test data' },
      { type: 'paragraph', text: 'Toggle to the Test Data tab to see auto-generated sample data for every node. A carousel lets you navigate between the Agent Input, each processing node, and the Agent Output. Each node displays a table with field names, types, and three example values across columns — giving you three different scenarios to verify against.' },
      { type: 'paragraph', text: 'Node tables are color-coded by type: cyan for inbound, blue for LLM processing, rose for integration nodes, and green for outbound. Output fields that are generated at runtime (not known ahead of time) display "To be generated during execution" in amber. This test data is reused in the Execute step as one of the available sample datasets.' },

      { type: 'callout', variant: 'tip', text: 'Data mapping is fully automatic — you typically don\'t need to change anything. Use the Variable Dictionary to understand connections and the Test Data tab to sanity-check the pipeline before moving forward.' },
    ],
  },
  {
    icon: Code,
    title: 'Generate Prompts',
    askQuestion: 'How are prompts generated? Can I customize them?',
    summary: 'Optimized AI instructions are generated for each LLM node — system prompt, user prompt, context, and output format. Review them side by side, test any node with sample data, and download the complete prompt set.',
    content: [
      { type: 'heading', text: 'Prompt structure' },
      { type: 'paragraph', text: 'Each LLM node in the pipeline receives two prompts generated by Byte:' },
      { type: 'list', items: [
        'System Prompt — defines the node\'s role, constraints, and behavioral guidelines. Sets the AI\'s "persona" for that step (e.g., "You are an email classification expert…").',
        'User Prompt — the actual instruction with schema enforcement. Includes the input data template, expected output format, and quality criteria.',
      ]},
      { type: 'paragraph', text: 'Both prompts are displayed in scrollable monospace blocks with one-click copy buttons. The prompts include context from the full pipeline — each node knows what came before it and what\'s expected after, which improves quality and coherence across the workflow.' },

      { type: 'heading', text: 'Navigating between nodes' },
      { type: 'paragraph', text: 'A stepper bar across the top of the screen lets you switch between LLM nodes. Click any step to load that node\'s prompts. Below the stepper, the full flow diagram is expandable with one click — this shows where the current node fits in the overall pipeline, making it easy to understand each node\'s role in context.' },

      { type: 'heading', text: 'Testing nodes in isolation' },
      { type: 'paragraph', text: 'The "Test This Node" panel lets you run any LLM node independently without executing the full pipeline. Click Run Test to execute the node with AI-generated sample input data. The output is displayed below the input panel.' },
      { type: 'paragraph', text: 'After the first test run, the input becomes editable — modify it freely and re-run to test edge cases, unusual inputs, or boundary conditions. Toggle "Request JSON output" when you need the node to return structured data instead of free-form text. Each node\'s test input and results persist independently — switching between nodes and back won\'t lose your previous test runs.' },

      { type: 'heading', text: 'Exporting prompts' },
      { type: 'paragraph', text: 'Click "Download Prompts" to export all system and user prompts as a JSON file. This is useful for offline review, sharing with team members, or integrating the prompts into your own application outside of ByteSizedAI Studio.' },

      { type: 'callout', variant: 'tip', text: 'Use "Request JSON output" when downstream systems expect structured data. Test with edge cases — enter minimal, empty, or unusual inputs to verify the node handles them gracefully. Results are saved per-node, so you can compare different test runs.' },
    ],
  },
  {
    icon: Play,
    title: 'Execute & Test',
    askQuestion: 'How does execution work? What is Brainwave quality scoring?',
    summary: 'Run your entire agent end-to-end and watch each node process in real time. Brainwave Self-Correction automatically detects and fixes low-quality outputs. Review past runs, compare results, and export data.',
    content: [
      { type: 'heading', text: 'Choosing sample data' },
      { type: 'paragraph', text: 'Before execution, choose from three auto-generated sample datasets. Each dataset contains realistic input values derived from your agent\'s schemas and node descriptions. Select the dataset that best represents a real-world scenario, then click Execute to start the pipeline.' },

      { type: 'heading', text: 'Real-time execution tracking' },
      { type: 'paragraph', text: 'Once execution begins, a sticky progress bar appears at the top of the screen showing each node as a labeled segment. The currently processing node pulses with an animated cyan glow. Below the progress track, clickable node step indicators let you jump directly to any node\'s results. A card carousel underneath displays each node\'s full input and output details.' },
      { type: 'paragraph', text: 'Each node transitions through visual states during execution:' },
      { type: 'list', items: [
        'Pending (gray) — waiting to process. The node hasn\'t received input yet.',
        'Running (cyan spinner) — currently executing. The progress bar segment pulses.',
        'Correcting (amber pulse) — Brainwave detected a quality issue and is auto-correcting the output.',
        'Completed (green checkmark) — finished successfully with an acceptable quality score.',
        'Failed (red) — the node encountered an error during processing.',
      ]},
      { type: 'paragraph', text: 'The carousel auto-scrolls to follow execution as each node completes. If you\'re reviewing a previous node\'s output and don\'t want to be scrolled away, click the "Stay here" button that appears during auto-scroll.' },

      { type: 'heading', text: 'Brainwave quality scoring' },
      { type: 'paragraph', text: 'Every completed node receives a Brainwave quality score from 0 to 100, displayed as a badge with a color-coded tier:' },
      { type: 'list', items: [
        'Green (good) — the output meets quality standards. No action needed.',
        'Amber (needs attention) — the output has issues. Brainwave Self-Correction kicks in automatically.',
        'Red (poor) — significant quality problems detected.',
      ]},
      { type: 'paragraph', text: 'Each node also receives a TL;DR — a one-line plain-English summary of what the node produced. For example: "Classified the email as high-urgency billing inquiry and extracted 4 key entities." This makes it easy to scan results without reading the full output.' },

      { type: 'heading', text: 'Brainwave Self-Correction' },
      { type: 'paragraph', text: 'When a node scores below the quality threshold, Brainwave Self-Correction activates automatically. The node\'s card glows amber, a correction indicator appears, and the output is replaced with an improved version. Expand the "Corrections" section on the card to see exactly what was wrong with the original output and what changed in the corrected version.' },
      { type: 'paragraph', text: 'Self-correction happens in real time during execution — you don\'t need to manually re-run anything. The pipeline continues with the corrected output flowing into the next node.' },

      { type: 'heading', text: 'Past runs and export' },
      { type: 'paragraph', text: 'The Past Runs sidebar stores up to 30 previous executions with timestamps. Click any past run to reload its full results into the carousel — including all inputs, outputs, quality scores, and corrections. This makes it easy to compare results across different sample datasets or configuration changes.' },
      { type: 'paragraph', text: 'Click the download button to export all node inputs and outputs as CSV for offline analysis, reporting, or integration with other tools.' },

      { type: 'callout', variant: 'tip', text: 'Run the agent with all three sample datasets to test consistency. If Brainwave flags a node repeatedly, consider going back to the Prompts tab and refining that node\'s instructions. Check the "Corrections" panel to understand what Brainwave is fixing — this often reveals prompt improvement opportunities.' },
    ],
  },
  {
    icon: Rocket,
    title: 'Prototype & Share',
    askQuestion: 'What is the prototype? How do I customize and share it?',
    summary: 'A branded, white-labeled application UI is auto-generated from your agent. Customize the look and feel through chat, test it with real data, then generate share links so stakeholders can try the agent themselves.',
    content: [
      { type: 'heading', text: 'Auto-generated application UI' },
      { type: 'paragraph', text: 'The prototype generates a complete, functional application interface from your agent configuration. It includes a branded header with your app name and description, a config-driven input form with labeled fields matching your agent\'s input schema, a submit button, and an intelligent output renderer. The result looks like a real product — not a development tool or demo.' },

      { type: 'heading', text: 'White-label customization' },
      { type: 'paragraph', text: 'Every visual element of the prototype is customizable:' },
      { type: 'list', items: [
        'App name — the title displayed in the prototype header.',
        'Description — a subtitle or tagline shown below the app name.',
        'Primary color — the brand color used for buttons, accents, and highlights.',
        'Logo — upload a custom image (auto-resized to 128×128) or use the default.',
        'Theme — switch between light and dark mode.',
        'Submit button label — customize the text (e.g., "Analyze Report", "Score Lead", "Generate Summary").',
        'Input field labels and placeholders — each form field can be renamed.',
      ]},
      { type: 'paragraph', text: 'Changes can be made through the settings panel, or by chatting with Byte directly. Say "make it dark mode," "change the app name to InboxAI," or "use a blue color scheme" — changes apply instantly to the live preview.' },

      { type: 'heading', text: 'Intelligent output rendering' },
      { type: 'paragraph', text: 'The prototype\'s output renderer automatically detects the content type and formats it appropriately:' },
      { type: 'list', items: [
        'Markdown — headings, bold, italic, lists, and code blocks are rendered as styled HTML.',
        'Tables — tabular data is displayed as a formatted table with headers.',
        'Key-value pairs — structured results appear as labeled cards.',
        'JSON — raw JSON is displayed in a syntax-highlighted code block.',
      ]},
      { type: 'paragraph', text: 'You don\'t need to configure the renderer — it inspects the output and chooses the best format automatically.' },

      { type: 'heading', text: 'Testing in the prototype' },
      { type: 'paragraph', text: 'Click "Try with sample data" to pre-fill all input fields with test data from your agent pipeline. Hit submit to execute the agent end-to-end and see the output rendered in the prototype interface. The embedded help chat lets end-users (or you) ask questions about the application — useful for verifying the experience before sharing.' },

      { type: 'heading', text: 'Sharing with stakeholders' },
      { type: 'paragraph', text: 'Click "Generate Share Link" to create a unique URL. The link is copied to your clipboard instantly. Anyone with the link sees your branded prototype as a standalone page — they can fill in their own inputs and run the agent live. No login required, no studio access needed. This makes it easy to get feedback from stakeholders, demo to clients, or test with real users before committing to a full build.' },

      { type: 'callout', variant: 'tip', text: 'Customize the prototype to match the stakeholder\'s brand before sharing — change the app name, colors, and logo so it feels like their product. The share link gives them a hands-on experience that\'s far more convincing than a slide deck.' },
    ],
  },
];

const USE_CASES_ROW1 = [
  {
    icon: Mail,
    title: 'Email Triage Agent',
    desc: 'Categorize emails by urgency, route to departments, draft responses.',
    prompt: 'Build an email triage agent that reads incoming customer emails, categorizes them by urgency (high, medium, low) and department (billing, technical, general), and drafts a response.',
  },
  {
    icon: Users,
    title: 'User Persona Generator',
    desc: 'Generate detailed personas from product descriptions and market data.',
    prompt: 'Create a user persona generator that takes a product description and target market, then generates 3 detailed user personas with demographics, goals, frustrations, and behaviors.',
  },
  {
    icon: BarChart3,
    title: 'Lead Scoring Pipeline',
    desc: 'Score and rank leads based on engagement signals and fit criteria.',
    prompt: 'Build a lead scoring agent that takes company information and engagement data, scores each lead from 0-100 based on fit and intent signals, and provides a prioritized recommendation.',
  },
  {
    icon: FileText,
    title: 'Report Summarizer',
    desc: 'Extract key insights from documents and produce executive summaries.',
    prompt: 'Create a report summarizer that takes a long document or article, extracts the key findings, data points, and recommendations, and produces a concise executive summary.',
  },
  {
    icon: Shield,
    title: 'Content Moderator',
    desc: 'Review content for policy violations and recommend actions.',
    prompt: 'Build a content moderation agent that reviews user-generated text, checks it against content policies (hate speech, spam, misinformation), assigns a risk score, and recommends an action (approve, flag, remove).',
  },
  {
    icon: Search,
    title: 'Competitive Analyzer',
    desc: 'Analyze competitors and generate comparison reports with insights.',
    prompt: 'Create a competitive analysis agent that takes a product description and competitor names, researches their positioning, and generates a comparison matrix with strategic recommendations.',
  },
];

const USE_CASES_ROW2 = [
  {
    icon: Zap,
    title: 'Onboarding Automator',
    desc: 'Walk new users through setup steps and personalize their experience.',
    prompt: 'Build an onboarding agent that asks new users about their goals, recommends features, and generates a personalized getting-started checklist.',
  },
  {
    icon: Code,
    title: 'Code Review Assistant',
    desc: 'Analyze pull requests for bugs, style issues, and security concerns.',
    prompt: 'Create a code review agent that takes a code diff, identifies potential bugs and security issues, checks against style guidelines, and provides actionable feedback.',
  },
  {
    icon: Presentation,
    title: 'Pitch Deck Reviewer',
    desc: 'Evaluate startup pitches and suggest improvements slide by slide.',
    prompt: 'Build a pitch deck reviewer that analyzes each slide of a startup pitch, scores clarity and persuasiveness, and suggests specific improvements for each section.',
  },
  {
    icon: MessageSquare,
    title: 'Customer Feedback Analyzer',
    desc: 'Cluster feedback themes, detect sentiment, and surface top requests.',
    prompt: 'Create a feedback analysis agent that takes raw customer feedback, clusters it by theme, detects sentiment, and produces a prioritized list of the top feature requests and pain points.',
  },
  {
    icon: GitBranch,
    title: 'Workflow Builder',
    desc: 'Convert SOPs into automated step-by-step workflows.',
    prompt: 'Build an agent that takes a standard operating procedure document, breaks it into discrete steps, identifies decision points, and generates an automated workflow diagram.',
  },
  {
    icon: Sliders,
    title: 'A/B Test Planner',
    desc: 'Design experiments with hypotheses, metrics, and sample size estimates.',
    prompt: 'Create an A/B test planning agent that takes a feature description, generates hypotheses, recommends metrics to track, calculates required sample sizes, and outlines the test plan.',
  },
];

const ROTATING_EXAMPLES = [
  'email triage agent',
  'lead scoring pipeline',
  'report summarizer',
  'content moderator',
  'persona generator',
];

// ── Use-case marquee card ────────────────────────────────────────────

function UseCaseCard({ useCase, dark, onBuildUseCase }) {
  const UCIcon = useCase.icon;
  return (
    <SpotlightCard
      spotlightColor={dark ? 'rgba(8, 145, 178, 0.15)' : 'rgba(8, 145, 178, 0.08)'}
      className={`w-[300px] flex-shrink-0 rounded-xl transition-all cursor-pointer group ${dark
        ? 'bg-gray-800/40 hover:bg-gray-800/70 border border-gray-800 hover:border-gray-700'
        : 'bg-white hover:shadow-md border border-gray-100 hover:border-gray-200'
      }`}
    >
      <button
        onClick={() => onBuildUseCase(useCase.prompt)}
        className="w-full p-5 text-left"
      >
        <UCIcon className={`w-6 h-6 mb-3 ${dark ? 'text-gray-600 group-hover:text-cyan-400' : 'text-gray-400 group-hover:text-cyan-600'} transition-colors`} />
        <h3 className={`text-base font-semibold mb-1.5 ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
          <DecryptedText
            text={useCase.title}
            animateOn="hover"
            speed={30}
            sequential
            revealDirection="start"
            className={dark ? 'text-gray-200' : 'text-gray-800'}
            encryptedClassName={dark ? 'text-cyan-500/50' : 'text-cyan-400/60'}
          />
        </h3>
        <p className={`text-sm leading-relaxed mb-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
          {useCase.desc}
        </p>
        <span className={`inline-flex items-center space-x-1 text-sm font-semibold ${dark ? 'text-cyan-400/70 group-hover:text-cyan-400' : 'text-cyan-600/70 group-hover:text-cyan-700'} transition-colors`}>
          <span>Build this</span>
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </button>
    </SpotlightCard>
  );
}

// ── Component ────────────────────────────────────────────────────────

export default function DocumentationView({ isDarkMode, onAskByte, onBuildUseCase, onStartBuilding, jiraConnected, onFetchFromJira, onConnectJira }) {
  const dark = isDarkMode;
  const [activeStep, setActiveStep] = useState(1);

  return (
    <div className="space-y-8 pb-10">

      {/* ── Hero with FloatingLines background ────────────────────── */}
      <div className="relative rounded-2xl overflow-hidden -mx-4"
        style={{ background: dark ? '#050a12' : 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 40%, #e0f2fe 100%)' }}>
        {/* WebGL background — subtle, behind content */}
        <div className="absolute inset-0" style={{ opacity: dark ? 0.4 : 0.15 }}>
          <FloatingLines
            linesGradient={dark ? ['#0e7490', '#0891b2', '#06b6d4', '#22d3ee'] : ['#0e7490', '#0891b2', '#06b6d4']}
            enabledWaves={['middle', 'bottom']}
            lineCount={[4, 3]}
            lineDistance={[3, 4]}
            animationSpeed={0.6}
            interactive={true}
            bendRadius={4.0}
            bendStrength={-0.3}
            mouseDamping={0.04}
            parallax={true}
            parallaxStrength={0.12}
          />
        </div>
        {/* Center fade overlay for text readability */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: dark
            ? 'radial-gradient(ellipse at center, rgba(5,10,18,0.7) 0%, transparent 70%)'
            : 'radial-gradient(ellipse at center, rgba(240,253,250,0.8) 0%, transparent 70%)',
        }} />

        {/* Hero content — pointer-events-none so mouse reaches canvas, re-enabled on buttons */}
        <div className="relative z-10 pt-10 pb-10 px-4 flex flex-col items-center pointer-events-none">
          {/* Lead with the problem — 85% stat */}
          <div className="flex items-baseline justify-center space-x-1 mb-1">
            <span className={`text-5xl font-black tabular-nums tracking-tight ${dark ? 'text-white' : 'text-gray-900'}`}>
              <CountUp from={0} to={85} duration={2.5} />
              <span className={`text-3xl ${dark ? 'text-cyan-400' : 'text-cyan-600'}`}>%</span>
            </span>
          </div>
          <p className={`text-sm mb-6 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
            of AI projects fail to deliver intended value
            <span className={`ml-1.5 ${dark ? 'text-gray-600' : 'text-gray-400'}`}>— Gartner</span>
          </p>

          {/* TrueFocus headline */}
          <div className="mb-5">
            <TrueFocus
              sentence="Prove it works|before you invest."
              separator="|"
              manualMode={false}
              blurAmount={4}
              borderColor={dark ? '#06b6d4' : '#0891b2'}
              glowColor={dark ? 'rgba(6, 182, 212, 0.5)' : 'rgba(8, 145, 178, 0.3)'}
              animationDuration={0.5}
              pauseBetweenAnimations={1.8}
              className={`text-3xl font-bold leading-tight ${dark ? 'text-white' : 'text-gray-900'}`}
            />
          </div>

          {/* Dynamic rotating use case */}
          <FadeIn delay={0.3} duration={0.6}>
            <div className="flex items-baseline justify-center space-x-2 mb-8 flex-wrap">
              <span className={`text-base ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Will a</span>
              <RotatingText
                texts={ROTATING_EXAMPLES}
                rotationInterval={2500}
                splitBy="characters"
                staggerDuration={0.025}
                staggerFrom="first"
                mainClassName={`text-xl font-bold ${dark ? 'text-cyan-400' : 'text-cyan-600'}`}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-100%', opacity: 0 }}
              />
              <span className={`text-base ${dark ? 'text-gray-400' : 'text-gray-500'}`}>work? Find out before the sprint begins.</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.6} duration={0.5}>
            <div className="flex flex-col items-center space-y-4 pointer-events-auto">
              {/* Primary CTAs — Jira + Manual */}
              <div className="flex items-center justify-center space-x-3">
                <StarBorder as="div" color={dark ? '#a855f7' : '#7c3aed'} speed="5s">
                  <button
                    onClick={() => {
                      if (jiraConnected) {
                        onFetchFromJira?.();
                      } else {
                        onConnectJira?.();
                      }
                    }}
                    className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24.013 12.487V1.005A1.005 1.005 0 0 0 23.013 0z"/>
                    </svg>
                    <span>{jiraConnected ? 'Fetch from Jira' : 'Connect Jira'}</span>
                  </button>
                </StarBorder>
                <StarBorder as="div" color={dark ? '#06b6d4' : '#0891b2'} speed="5s">
                  <button
                    onClick={onStartBuilding}
                    className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #0891b2, #0e7490)' }}
                  >
                    <Rocket className="w-4 h-4" />
                    <span>Describe Manually</span>
                  </button>
                </StarBorder>
              </div>
              {/* Secondary CTAs */}
              <div className="flex items-center justify-center space-x-3">
                <button
                  onClick={() => onAskByte('What can I build with ByteSizedAI Studio? Give me some examples.')}
                  className={`inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] ${dark ? 'text-white' : 'text-gray-700'}`}
                  style={{
                    background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.7)',
                    border: dark ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(8,145,178,0.2)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Ask Byte</span>
                </button>
                <button
                  onClick={() => document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth' })}
                  className={`inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] ${dark ? 'text-white' : 'text-gray-700'}`}
                  style={{
                    background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.7)',
                    border: dark ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(8,145,178,0.2)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <span>Try a Use Case</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ── How It Works — Stepper + Documentation ─────────────── */}
      <FadeIn>
        <div>
          <h2 className={`text-xl font-bold mb-1 ${dark ? 'text-white' : 'text-gray-900'}`}>How it works</h2>
          <p className={`text-sm mb-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
            Click any step to read the full documentation
          </p>

          <Stepper
            initialStep={1}
            accentColor="#0891b2"
            dark={dark}
            onStepChange={(step) => setActiveStep(step)}
          >
            {STEPS.map((step) => (
              <Step key={step.title}>
                <div className="py-4">
                  {/* Step header */}
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(8,145,178,0.1)' }}>
                      <step.icon className="w-5 h-5" style={{ color: '#0891b2' }} />
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>
                        {step.title}
                      </h3>
                    </div>
                  </div>

                  {/* Lead summary + Byte link */}
                  <p className={`text-[15px] leading-relaxed mb-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {step.summary}
                  </p>
                  <button
                    onClick={() => onAskByte(step.askQuestion)}
                    className={`inline-flex items-center space-x-1.5 text-sm font-medium mb-5 transition-colors ${dark
                      ? 'text-cyan-400/80 hover:text-cyan-300'
                      : 'text-cyan-600 hover:text-cyan-700'
                    }`}
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>Discuss this with Byte</span>
                  </button>

                  {/* Rich content blocks */}
                  {step.content && step.content.map((block, i) => {
                    if (block.type === 'heading') {
                      return (
                        <h4 key={i} className={`text-sm font-bold uppercase tracking-wide mt-5 mb-2 ${dark ? 'text-gray-300' : 'text-gray-800'}`}>
                          {block.text}
                        </h4>
                      );
                    }
                    if (block.type === 'paragraph') {
                      return (
                        <p key={i} className={`text-sm leading-[1.7] mb-3 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {block.text}
                        </p>
                      );
                    }
                    if (block.type === 'list') {
                      return (
                        <ul key={i} className={`mb-3 space-y-1.5 ml-1 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {block.items.map((item, j) => (
                            <li key={j} className="flex items-start space-x-2 text-sm leading-[1.7]">
                              <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#0891b2' }} />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    if (block.type === 'callout') {
                      const isTip = block.variant === 'tip';
                      const isWarning = block.variant === 'warning';
                      const CalloutIcon = isTip ? Lightbulb : isWarning ? AlertCircle : Info;
                      return (
                        <div key={i} className={`flex items-start space-x-2.5 rounded-lg px-3.5 py-3 mt-4 mb-2 ${
                          isTip
                            ? dark ? 'bg-cyan-950/30 border border-cyan-900/20' : 'bg-cyan-50/60 border border-cyan-100'
                            : isWarning
                              ? dark ? 'bg-amber-950/30 border border-amber-900/20' : 'bg-amber-50/60 border border-amber-100'
                              : dark ? 'bg-blue-950/30 border border-blue-900/20' : 'bg-blue-50/60 border border-blue-100'
                        }`}>
                          <CalloutIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: isTip ? '#0891b2' : isWarning ? '#d97706' : '#3b82f6'
                          }} />
                          <div>
                            <span className={`text-xs font-bold uppercase tracking-wide ${
                              isTip
                                ? dark ? 'text-cyan-400' : 'text-cyan-700'
                                : isWarning
                                  ? dark ? 'text-amber-400' : 'text-amber-700'
                                  : dark ? 'text-blue-400' : 'text-blue-700'
                            }`}>
                              {isTip ? 'Tip' : isWarning ? 'Warning' : 'Note'}
                            </span>
                            <p className={`text-sm leading-[1.7] mt-0.5 ${
                              isTip
                                ? dark ? 'text-cyan-300/70' : 'text-cyan-800/80'
                                : isWarning
                                  ? dark ? 'text-amber-300/70' : 'text-amber-800/80'
                                  : dark ? 'text-blue-300/70' : 'text-blue-800/80'
                            }`}>
                              {block.text}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}

                </div>
              </Step>
            ))}
          </Stepper>
        </div>
      </FadeIn>

      {/* ── Divider ───────────────────────────────────────────────── */}
      <div className={`h-px`}
        style={{ background: dark ? '#1f2937' : 'linear-gradient(90deg, transparent, #e5e7eb, transparent)' }} />

      {/* ── Use Cases — Alternating Marquee ──────────────────────── */}
      <FadeIn>
        <div id="use-cases">
          <h2 className={`text-xl font-bold mb-1 ${dark ? 'text-white' : 'text-gray-900'}`}>Try a use case</h2>
          <p className={`text-sm mb-5 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
            Hover to pause, click to build
          </p>

          <div className="space-y-3 -mx-4">
            {/* Row 1 — scrolls left */}
            <Marquee speed={25} direction="left" pauseOnHover gap={12}>
              {USE_CASES_ROW1.map((uc, i) => (
                <UseCaseCard key={i} useCase={uc} dark={dark} onBuildUseCase={onBuildUseCase} />
              ))}
            </Marquee>

            {/* Row 2 — scrolls right */}
            <Marquee speed={20} direction="right" pauseOnHover gap={12}>
              {USE_CASES_ROW2.map((uc, i) => (
                <UseCaseCard key={i} useCase={uc} dark={dark} onBuildUseCase={onBuildUseCase} />
              ))}
            </Marquee>
          </div>
        </div>
      </FadeIn>

      {/* ── Bottom CTA ───────────────────────────────────────────── */}
      <FadeIn>
        <div className="pt-2 pb-4 text-center">
          <p className={`text-base mb-4 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
            Ready to validate your idea?
          </p>
          <div className="flex items-center justify-center space-x-3">
            <StarBorder as="div" color={dark ? '#a855f7' : '#7c3aed'} speed="5s">
              <button
                onClick={() => {
                  if (jiraConnected) {
                    onFetchFromJira?.();
                  } else {
                    onConnectJira?.();
                  }
                }}
                className="inline-flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24.013 12.487V1.005A1.005 1.005 0 0 0 23.013 0z"/>
                </svg>
                <span>{jiraConnected ? 'Fetch from Jira' : 'Connect Jira'}</span>
              </button>
            </StarBorder>
            <StarBorder as="div" color="#06b6d4" speed="5s">
              <button
                onClick={onStartBuilding}
                className="inline-flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #0891b2, #0e7490)' }}
              >
                <Rocket className="w-4 h-4" />
                <span>Describe Manually</span>
              </button>
            </StarBorder>
          </div>
        </div>
      </FadeIn>
    </div>
  );
}
